import React from "react";
import { StyleSheet } from "react-native";
import { Query } from "react-apollo";
import { Grid } from "react-native-responsive-layout";
import styled, { withTheme } from "styled-components/native";
import { Form, ToastContext, ShiftView } from "cci-component";
import { withRouter } from "cci-utility";
import { InspectTitle } from "../layout/Titles";

/* Wrapper for the whole inspect component */
const Wrapper = styled(ShiftView)`
  padding-top: ${props => props.marginTop || props.theme.pageContentMarginTop}px;
  flex: 1;
`;

const Footer = styled.View`
  padding: ${props => props.theme.inspectFooterPadding || "20px 30px"};
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

/**
 * Inspect component that loads a record and provides all of the utilities to show the information related to it.
 *
 * This inspect is immutable which means it cannot be changed. For an inspect that allows the user to make changes, use MutableInspect.
 *
 * Props:
 * - title: Title that goes above all of the fields in the inspect.
 * - defaultValues: An object containing the values that must be filled out in the certain fields when a record is new.
 * - query: GraphQL query that will be used to fetch the detail of the record from the server
 * - queryName: Name of the query so that the data can be extracted from the query.
 * - recordId: Primary key of the record that must be fetched.
 */
export const ImmutableInspect = ({
  theme,
  title,
  match,
  defaultValues,
  query,
  queryName,
  myId,
  recordId = match.params.id || myId,
  style,
  mdStyle,
  marginTop,
  fixedFooter,
  negativeMargin,
  onQueryCompleted = () => {},
  setLoading = () => {},
  fetchPolicy,
  ...restProps
}) => {
  const styles = StyleSheet.create({
    // Applicable when a phone
    gridStyle: {
      alignItems: "stretch",
      padding: 20
    },

    // TODO for TITLES and SUBTITLES: https://stackoverflow.com/questions/51664256/negative-margintop-not-working-as-intended-on-android-works-fine-in-ios
    // Landscape phone or greater
    gridMdStyle: {
      alignItems: "stretch",
      alignSelf: "center",
      width: theme.inspectPercentage || "65%"
    }
  });

  return (
    <ToastContext.Consumer>
      {toast => (
        <Query
          query={query}
          variables={{ id: recordId }}
          skip={myId === "new" || recordId === "new"}
          onCompleted={onQueryCompleted}
          fetchPolicy={fetchPolicy}
        >
          {({ loading, error, data }) => {
            /* For what this line does, see "Computed object property names and destructuring" in
          https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Destructuring_assignment

          When a user type a specific URL to which does not have permissions, 'data' comes empty.
          */
            const queryData = (data && data[queryName]) || defaultValues,
              initialValues =
                myId === "new" || recordId === "new"
                  ? defaultValues
                  : queryData;

            return (
              <Form
                showMessage={toast.show}
                initialValues={initialValues}
                {...restProps}
              >
                {({ form, handleSubmit, pristine }) => (
                  <Wrapper marginTop={marginTop}>
                    <Grid
                      style={{ ...styles.gridStyle, ...style }}
                      mdStyle={[styles.gridMdStyle, mdStyle]}
                      scrollable
                      stretchable
                    >
                      {/* Page title */}
                      {title && (
                        <InspectTitle
                          title={title}
                          lgStyle={
                            negativeMargin === false && { marginLeft: 0 }
                          }
                        />
                      )}

                      {/* Content */}
                      {restProps.children({ form, handleSubmit, pristine, setLoading })}
                    </Grid>

                    {fixedFooter && (
                      <Footer>
                        {fixedFooter({ form, handleSubmit, pristine })}
                      </Footer>
                    )}
                  </Wrapper>
                )}
              </Form>
            );
          }}
        </Query>
      )}
    </ToastContext.Consumer>
  );
};

export default withTheme(withRouter(ImmutableInspect));
